import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Zap, Crown, ArrowRight, CreditCard, ChevronDown, type LucideIcon } from "lucide-react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { BookDemoModal } from "@/components/book-demo-modal";

interface Plan {
  name: string;
  nameSize: string;
  price: string;
  period: string;
  fee: string;
  feeLabel: string;
  icon: LucideIcon;
  description: string;
  features: { text: string; included: boolean }[];
  cta: string;
  popular: boolean;
}

const faqItems = [
  {
    question: "How does Open Mat work?",
    answer: "Open Mat is a SaaS platform that automates private lesson management for martial arts gyms. Gym owners sign up, add their trainers, set session rates and revenue splits, and configure cancellation policies. Students book directly with trainers, pay upfront, and everyone gets paid automatically after each session.",
  },
  {
    question: "How much does Open Mat cost?",
    answer: "Open Mat is free for gym owners — you can use the Free plan indefinitely with no monthly cost. An optional Premium plan ($49/month) unlocks advanced features like video sessions and multi-location support. Both plans include a small per-session processing fee — paid by the student, not the gym owner. On the Free plan it's $3 per session; on Premium it's $1. Premium comes with a 30-day free trial. Both plans include unlimited trainers, students, session booking, scheduling, packages, and automated payouts.",
  },
  {
    question: "Is Open Mat free for my gym?",
    answer: "Yes — gym owners pay nothing. There is no monthly subscription on the Free plan and no setup cost. The only fee is a small per-session processing charge ($3 on Free, $1 on Premium) which is paid by the student at the time of booking, not the gym owner. There's no credit card required to sign up, and you can use the Free plan for as long as you want. When you're ready for Premium, you can start with a free 30-day trial — cancel anytime during the trial without being charged.",
  },
  {
    question: "What payment methods does Open Mat support?",
    answer: "Open Mat processes payments through Stripe, supporting all major credit and debit cards including Visa, Mastercard, and American Express, as well as Apple Pay and Google Pay. A standard 2.9% + $0.30 Stripe processing fee applies per transaction — this is passed through at cost with no markup and is included in the per-session fee paid by the student.",
  },
  {
    question: "Can I manage multiple gym locations with Open Mat?",
    answer: "Yes — multi-location support is available on the Premium plan. You can manage all your trainers, sessions, and revenue across multiple locations from a single owner dashboard. Each location can have its own hours, trainers, and scheduling configuration.",
  },
  {
    question: "How do trainer payouts work?",
    answer: "When a session is completed, Open Mat automatically splits the payment between the gym and the trainer based on the revenue split you configure. Trainers receive their cut via Stripe Connect — no manual invoicing, no spreadsheets, no end-of-month reconciliation needed.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

function FAQItem({ item, isOpen, onToggle }: { item: typeof faqItems[0]; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-white/[0.06]">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <h3 className="text-base font-semibold text-white group-hover:text-primary transition-colors pr-4">
          {item.question}
        </h3>
        <ChevronDown className={`w-5 h-5 text-white/40 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-sm text-white/50 leading-relaxed pb-5">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);

  const plans: Plan[] = [
    {
      name: "Free",
      nameSize: "text-4xl",
      price: "$0",
      period: "/month",
      fee: "$3.00",
      feeLabel: "per session",
      icon: Zap,
      description: "Everything you need to start managing private lessons. No monthly commitment.",
      features: [
        { text: "Unlimited trainers & students", included: true },
        { text: "Session booking & scheduling", included: true },
        { text: "Session packages", included: true },
        { text: "Automated trainer payouts", included: true },
        { text: "Basic analytics", included: true },
        { text: "Video sessions", included: false },
        { text: "Multi-location support", included: false },
        { text: "White-label branding", included: false },
        { text: "Custom domain", included: false },
        { text: "Priority support", included: false },
      ],
      cta: "Get Started Free",
      popular: false,
    },
    {
      name: "Premium",
      nameSize: "text-3xl",
      price: "$49",
      period: "/month",
      fee: "$1.00",
      feeLabel: "per session",
      icon: Crown,
      description: "For established gyms ready to scale. First 30 days free — lower fees, more features, full control.",
      features: [
        { text: "Unlimited trainers & students", included: true },
        { text: "Session booking & scheduling", included: true },
        { text: "Session packages", included: true },
        { text: "Automated trainer payouts", included: true },
        { text: "Advanced analytics suite", included: true },
        { text: "Video sessions", included: true },
        { text: "Multi-location support", included: true },
        { text: "White-label branding", included: true },
        { text: "Custom domain", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Start Free Trial",
      popular: true,
    },
  ];

  return (
    <PublicLayout>
      <SEOHead
        title="Pricing — Free & Premium Plans for Martial Arts Gyms"
        description="Open Mat pricing: Free plan with $3/session fee, or Premium at $49/month with $1/session fee. No credit card required. Unlimited trainers and students on both plans."
        canonicalPath="/pricing"
        jsonLd={faqJsonLd}
      />

      <div className="flex-1 py-24 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-sm font-medium text-white/60">Free to get started — no credit card required</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white uppercase tracking-tight mb-5">
            Simple, <span className="text-primary">Transparent</span> Pricing
          </h1>
          <p className="text-lg text-white/50 max-w-2xl mx-auto">
            Two plans. No hidden fees. Start on Free, upgrade when you need more power.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className={`h-full border overflow-hidden rounded-2xl ${plan.popular ? "border-primary/40 shadow-2xl shadow-primary/10 bg-white/[0.03]" : "border-white/[0.06] bg-white/[0.02]"}`}>
                {plan.popular && (
                  <div className="bg-gradient-to-r from-primary to-[#f0d060] text-black text-center text-xs font-bold py-2 uppercase tracking-widest">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${plan.popular ? "bg-primary/15" : "bg-white/[0.06]"}`}>
                      <plan.icon className={`w-5 h-5 ${plan.popular ? "text-primary" : "text-white/70"}`} />
                    </div>
                    <h2 className={`font-bold text-white ${plan.nameSize}`}>{plan.name}</h2>
                  </div>

                  <p className="text-sm text-white/50 mb-6">{plan.description}</p>

                  <div className="mb-1">
                    <span className="text-5xl font-mono font-bold text-white">{plan.price}</span>
                    <span className="text-white/40 ml-1">{plan.period}</span>
                  </div>
                  {plan.popular && (
                    <p className="text-xs font-semibold text-emerald-400 mb-1">First 30 days free</p>
                  )}
                  <p className="text-sm text-primary font-semibold mb-8">
                    + {plan.fee} {plan.feeLabel} platform fee
                  </p>

                  <div className="space-y-3 mb-8">
                    {plan.features.map(f => (
                      <div key={f.text} className="flex items-center gap-3">
                        {f.included
                          ? <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                          : <X className="w-4 h-4 text-white/15 shrink-0" />
                        }
                        <span className={f.included ? "text-white/80 text-sm" : "text-white/25 text-sm"}>{f.text}</span>
                      </div>
                    ))}
                  </div>

                  <Link href="/register">
                    <Button className={`w-full h-13 text-base font-bold rounded-xl transition-all ${plan.popular
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20"
                      : "bg-white/[0.06] text-white border border-white/[0.08] hover:bg-white/[0.1]"
                    }`}>
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="max-w-4xl mx-auto mb-6"
        >
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <div className="px-8 pt-7 pb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-white/30 mb-1">How we compare</p>
              <h2 className="text-lg font-bold text-white">Open Mat vs. the competition</h2>
              <p className="text-sm text-white/40 mt-1">Gym management software built for martial arts, priced for gym owners.</p>
            </div>
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-white/[0.06]">
                    <th className="text-left px-8 py-3 text-white/30 font-semibold text-xs uppercase tracking-wider w-1/4">Platform</th>
                    <th className="px-6 py-3 text-white/30 font-semibold text-xs uppercase tracking-wider text-center">Monthly cost</th>
                    <th className="px-6 py-3 text-white/30 font-semibold text-xs uppercase tracking-wider text-center">Per-session fee</th>
                    <th className="px-6 py-3 text-white/30 font-semibold text-xs uppercase tracking-wider text-center">Who pays</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-primary/20 bg-primary/[0.04]">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-bold text-white">Open Mat Free</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono font-bold text-emerald-400">$0</span>
                      <span className="text-white/30 text-xs ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono text-white/70">$3.00</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs font-semibold text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">Student</span>
                    </td>
                  </tr>
                  <tr className="border-t border-primary/20 bg-primary/[0.06]">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-bold text-white">Open Mat Premium</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono font-bold text-primary">$49</span>
                      <span className="text-white/30 text-xs ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono text-white/70">$1.00</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs font-semibold text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">Student</span>
                    </td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="px-8 py-4">
                      <span className="font-medium text-white/50">Zen Planner</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono text-white/40">~$99</span>
                      <span className="text-white/20 text-xs ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-white/25 text-xs">—</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs font-semibold text-white/30 bg-white/[0.06] px-2 py-1 rounded-full">Gym owner</span>
                    </td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="px-8 py-4">
                      <span className="font-medium text-white/50">Mindbody</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-mono text-white/40">~$129</span>
                      <span className="text-white/20 text-xs ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-white/25 text-xs">—</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs font-semibold text-white/30 bg-white/[0.06] px-2 py-1 rounded-full">Gym owner</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="px-8 py-4 border-t border-white/[0.06] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <p className="text-xs text-white/35">Competitor pricing is approximate and subject to change. Open Mat fees are paid by students, not gym owners.</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-4xl mx-auto space-y-6"
        >
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-white/40" />
              <h2 className="text-lg font-bold text-white normal-case">Credit Card Processing</h2>
            </div>
            <p className="text-white/50 text-sm max-w-lg mx-auto mb-4">
              All transactions include a standard credit card processing fee of <span className="text-white font-semibold">2.9% + $0.30</span> per transaction, charged by the payment processor. This is passed through at cost — Open Mat does not mark up processing fees.
            </p>
            <div className="inline-flex items-center gap-4 text-xs text-white/30">
              <span>Visa</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Mastercard</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Amex</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Apple Pay</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Google Pay</span>
            </div>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-8 text-center">
            <p className="text-white font-bold text-lg mb-2">Premium pays for itself at ~25 sessions/month</p>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              At 25 sessions per month, the $2/session savings on Premium covers the $49 subscription. Every session after that puts more money in your pocket.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="max-w-4xl mx-auto mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center"
        >
          <p className="text-white font-bold text-lg mb-2">Not ready to sign up? See it in action first.</p>
          <p className="text-white/50 text-sm max-w-lg mx-auto mb-5">
            Schedule a personalized walkthrough and we'll show you exactly how Open Mat would work for your gym.
          </p>
          <Button
            onClick={() => setDemoOpen(true)}
            className="h-11 px-8 text-sm border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] font-semibold"
            style={{
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderRadius: 4,
            }}
          >
            Book a Demo
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto mt-20"
          id="faq"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="border-t border-white/[0.06]">
            {faqItems.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </motion.div>
      </div>

      <BookDemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </PublicLayout>
  );
}
